import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import get from 'lodash/get'
import { v4 as uuidv4 } from 'uuid'

import {
  BlockModel,
  NULL as BlockModelNULL,
} from '@client/components/Block/model'
import { createBlockModel } from '@client/components/Block'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { cancelStorage } from '@client/utils/Storage'
import {
  BLOCK_TYPES_SETTINGS_ENABLED,
  BLOCK_TYPES,
} from '@client/components/Block/constants'
import { showDialog as saveAsBlockDialogModel } from '@client/components/SaveAsBlockDialog'
import {
  ITEM_TYPE_SURVEY,
  WIZARD_TYPE_PAGE,
  WIZARD_TYPE_FORM,
  WIZARD_TYPE_SURVEY,
  ITEM_TYPE_CONFIRMATION_MAIL,
} from '@client/components/Editor/settings'

import './styles.scss'
import { SECTION_TYPES } from '../Section/constants'

@inject('app', 'editor', 'serverRender')
@observer
class Toolbar extends Component {
  static propTypes = {
    model: PropTypes.instanceOf(BlockModel),
    app: PropTypes.instanceOf(AppModel),
    editor: PropTypes.instanceOf(EditorModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    model: BlockModelNULL,
    app: AppModelNULL,
    editor: EditorModelNULL,
    serverRender: false,
  }

  handleDelete = (event) => {
    this.props.model.container.removeItem(this.props.model)
  }

  handleCopy = (event) => {
    const { model, editor } = this.props
    const index = model.index
    const data = this.props.model.serialize()
    // serialize() returns null for a block whose width has been corrupted to
    // 0 (or negative) — the same self-heal signal Column.serialize() uses to
    // drop such a block on save (FE-517). The duplicate button is hidden for
    // this case (render(), below); this guard is defense in depth in case
    // something else ever calls handleCopy directly.
    if (!data) return
    if (data.type === BLOCK_TYPES.FIELD) {
      data.inputName = uuidv4()
    }
    const newBlockModel = createBlockModel(data, editor)
    this.props.model.container.addItemAt(newBlockModel, index)
  }

  handleEdit = (event) => {
    const item = this.props.editor.serialize()
    cancelStorage.saveItem(item)
    this.props.app.setEditingSettings(this.props.model)
  }

  handleMoveUp = (event) => {
    const { model } = this.props
    const index = model.index - 1
    this.props.model.container.moveItemAt(model, index)
  }

  handleMoveDown = (event) => {
    const { model } = this.props
    const index = model.index + 1
    this.props.model.container.moveItemAt(model, index)
  }

  handleSave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const data = this.props.model.serialize()
    // See handleCopy above — same self-heal null case, same defense in depth
    // (the save button is hidden for this case in render(), below).
    if (!data) return
    const newBlockModel = createBlockModel(data, this.props.editor)

    saveAsBlockDialogModel(newBlockModel, this.props.editor.kind)
  }

  componentWillUnmount() {
    if (this.props.app.editingSettings === this.props.model) {
      this.props.app.setEditingSettings(BlockModelNULL)
    }
  }

  render() {
    const { model, app, count, serverRender, editor } = this.props
    const { formatMessage } = app
    const show = model.showToolbar ? 'show' : ''
    const hideIcons = model.hideIcons ? 'hide-icons' : ''
    // Duplicate/save both need a serializable block (see handleCopy/handleSave)
    // — a block whose width has self-heal-corrupted to 0/negative can't be, so
    // those two actions are hidden for it rather than offered and then failing
    // silently. Edit/delete/move stay available so the block can still be fixed
    // or removed. Uses the narrow canSerialize computed (width only) rather
    // than calling serialize() here, so this render doesn't turn into a MobX
    // observer of every setting on the block.
    const canDuplicateOrSave = model.canSerialize

    return (
      <If condition={!serverRender}>
        <div className={`toolbar ${show} ${hideIcons}`}>
          <div className="buttons-secondary">
            {model.type === BLOCK_TYPES.SECTION && model.index !== 0 && (
              <div
                title={formatMessage('toolbar.button.moveUp.title')}
                className="move-up"
                onClick={this.handleMoveUp}
              >
                <span className="bp6-icon-standard bp6-icon-arrow-up"></span>
              </div>
            )}
            {model.type === BLOCK_TYPES.SECTION && model.index < count - 1 && (
              <div
                title={formatMessage('toolbar.button.moveDown.title')}
                className="move-down"
                onClick={this.handleMoveDown}
              >
                <span className="bp6-icon-standard bp6-icon-arrow-down"></span>
              </div>
            )}
          </div>
          <div className="buttons">
            <div
              title={formatMessage('toolbar.button.edit.title')}
              className="edit"
              onClick={this.handleEdit}
            >
              <span className="bp6-icon-standard bp6-icon-edit"></span>
            </div>
            {canDuplicateOrSave &&
              BLOCK_TYPES_SETTINGS_ENABLED[model.type] &&
              model.type !== BLOCK_TYPES.PRODUCT &&
              model.type !== BLOCK_TYPES.CONFIRM_BUTTON &&
              !(
                model.type === BLOCK_TYPES.FORM &&
                get(app, 'wizard.type') === WIZARD_TYPE_FORM
              ) &&
              !(
                (model.type === BLOCK_TYPES.SECTION ||
                  model.type === BLOCK_TYPES.SUBMIT_BUTTON) &&
                editor.kind === ITEM_TYPE_SURVEY
              ) && (
                <div
                  title={formatMessage('toolbar.button.duplicate.title')}
                  className="duplicate"
                  onClick={this.handleCopy}
                >
                  <span className="bp6-icon-standard bp6-icon-duplicate"></span>
                </div>
              )}
            {canDuplicateOrSave &&
              model.type !== BLOCK_TYPES.PRODUCT &&
              !(
                get(app, 'wizard.type') === WIZARD_TYPE_PAGE ||
                get(app, 'wizard.type') === WIZARD_TYPE_FORM ||
                get(app, 'wizard.type') === WIZARD_TYPE_SURVEY
              ) &&
              (get(app, 'isOnTrial') || get(app, 'isOnStandardPlan')) && (
                <div
                  title={formatMessage('toolbar.button.save.title')}
                  className="save"
                  onClick={this.handleSave}
                >
                  <span className="bp6-icon-standard bp6-icon-floppy-disk"></span>
                </div>
              )}
            {model.type === BLOCK_TYPES.PRODUCT ||
            (model.type === BLOCK_TYPES.FORM &&
              editor.numberOfFormBlocks === 1) ||
            (model.type === BLOCK_TYPES.CONFIRM_BUTTON &&
              editor.kind === ITEM_TYPE_CONFIRMATION_MAIL) ||
            ((model.type === BLOCK_TYPES.SECTION ||
              model.type === BLOCK_TYPES.SUBMIT_BUTTON) &&
              editor.kind === ITEM_TYPE_SURVEY) ? null : (
              <div
                title={formatMessage('toolbar.button.delete.title')}
                className="delete"
                onClick={this.handleDelete}
              >
                <span className="bp6-icon-standard bp6-icon-trash"></span>
              </div>
            )}
            {!(
              (model.type === BLOCK_TYPES.SECTION ||
                model.type === BLOCK_TYPES.SUBMIT_BUTTON) &&
              editor.kind === ITEM_TYPE_SURVEY
            ) &&
              model.type !== BLOCK_TYPES.PRODUCT && (
                <div
                  title={formatMessage('toolbar.button.move.title')}
                  className="move"
                  data-handler={true}
                >
                  <span className="bp6-icon-standard bp6-icon-move"></span>
                </div>
              )}
          </div>
          <div className="toolbar-type">
            {model.sectionType === SECTION_TYPES.PRODUCTSTHREECOL ||
            model.sectionType === SECTION_TYPES.PRODUCTSTWOCOL
              ? formatMessage('sectionblock.product-three-columns.text')
              : null}
            {model.sectionType !== SECTION_TYPES.PRODUCTSTWOCOL &&
            model.sectionType !== SECTION_TYPES.PRODUCTSTHREECOL ? (
              <>
                {model.type}
                {model.fieldType ? ` - ${model.fieldType}` : ''}
              </>
            ) : null}
          </div>
        </div>
      </If>
    )
  }
}

export default Toolbar
